/**
 * @license
 * Copyright TWISS.IO
 * All Rights Reserved.
 * Licensed under the MIT License.
 */

import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Observable } from 'rxjs';

import { map, startWith } from 'rxjs/operators';
import { ApiService } from '@app/@core/services';
import { Associate } from '@app/models';
import { MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from 'moment';
// tslint:disable-next-line:no-duplicate-imports
import {default as _rollupMoment} from 'moment';

const moment = _rollupMoment || _moment;

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const products_edit_options_date_formats = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'admin-products-edit-options-dialog',
  templateUrl: './products-edit-options-dialog.component.html',
  styleUrls: ['./products-edit-options-dialog.component.scss'],
  providers: [
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: {useUtc: true} },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: products_edit_options_date_formats },
  ],
})
export class AdminProductsEditOptionsDialogComponent {
  processing;
  product_cat;
  preDefinedPremiumTerms: boolean = false;
  premiumTerms: number[] = [];
  premiumTermsTouched: boolean = false;
  termError: string = '';

  authForm = this.formBuilder.group({
    'provider_slug': [''],
    'product_uuid': [''],
    'name': ['', Validators.required],
    'product_cat_slug': ['', Validators.required],
    'premium_type': ['', Validators.required],
    'participating': ['', Validators.required],
    'date_start': [new Date(), Validators.required],
    'date_end': [new Date(), Validators.required],
  });

  get name() { return this.authForm.controls.name; }
  get product_cat_slug() { return this.authForm.controls.product_cat_slug; }
  get premium_type() { return this.authForm.controls.premium_type; }
  get participating() { return this.authForm.controls.participating; }
  get date_start() { return this.authForm.controls.date_start; }
  get date_end() { return this.authForm.controls.date_end; }
  get isPremiumTermsInvalid(): boolean {
    return this.preDefinedPremiumTerms && this.premiumTerms.length === 0;
  }

  constructor (
    @Inject(MAT_DIALOG_DATA) private injected_data: any,
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    public dialogRef: MatDialogRef<AdminProductsEditOptionsDialogComponent>) {
    this.product_cat = this.injected_data['product_cat'];
    this.authForm.controls.provider_slug.setValue(this.injected_data['provider_slug']);
    this.authForm.controls.product_uuid.setValue(this.injected_data['product_uuid']);
    // 
    this.authForm.controls.name.setValue(this.injected_data['product_option'].name);
    this.authForm.controls.product_cat_slug.setValue(this.injected_data['product_option'].product_cat_slug);
    this.authForm.controls.premium_type.setValue(this.injected_data['product_option'].premium_type);
    this.authForm.controls.participating.setValue(this.injected_data['product_option'].participating);
    this.authForm.controls.date_start.setValue(this.injected_data['product_option'].date_start);
    this.authForm.controls.date_end.setValue(this.injected_data['product_option'].date_end);

    // Load saved premium terms
    const savedTerms = this.injected_data['product_option'].premium_terms;
    if (savedTerms && Array.isArray(savedTerms) && savedTerms.length > 0) {
      this.premiumTerms = savedTerms;
      this.preDefinedPremiumTerms = true;
    }
  }

  resetForm(ref = null) {
    if (!ref) {
      this.authForm.reset();
      this.preDefinedPremiumTerms = false;
      this.premiumTerms = [];
      this.premiumTermsTouched = false;
      this.termError = '';
    } else {
      const form_field = this.authForm.get(ref);
      if (!form_field.validator) return form_field.reset();

      const validator = form_field.validator({} as AbstractControl);
      if (validator && validator.required) form_field.setValue('');
      else form_field.reset();
    }
  }

  onPremiumTypeChange(event: any): void {
    // If premium type changed to 'single', reset premium terms
    if (event.value === 'single') {
      this.preDefinedPremiumTerms = false;
      this.premiumTerms = [];
      this.premiumTermsTouched = false;
      this.termError = '';
    }
  }

  resetPremiumTerms(ref = null) {
    if (!ref) {
      this.preDefinedPremiumTerms = false;
      this.premiumTerms = [];
      this.premiumTermsTouched = false;
      this.termError = '';
    }
  }

  addPremiumTermFromInput(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value ? input.value.trim() : '';
    this.termError = '';

    if (!value) return;

    const numValue = Number(value);

    if (isNaN(numValue) || !Number.isInteger(numValue)) {
      this.termError = 'Please enter a valid whole number.';
      event.preventDefault();
      return;
    }

    if (numValue <= 1 || numValue >=100) {
      this.termError = 'Valid number from 2-99';
      event.preventDefault();
      return;
    }

    if (this.premiumTerms.includes(numValue)) {
      this.termError = 'This term already exists.';
      event.preventDefault();
      return;
    }

    this.premiumTerms.push(numValue);
    this.premiumTerms.sort((a, b) => a - b);
    input.value = '';
    event.preventDefault();
  }

  removePremiumTerm(term: number): void {
    const index = this.premiumTerms.indexOf(term);
    if (index >= 0) {
      this.premiumTerms.splice(index, 1);
    }
  }

  submitForm() {
    // Mark premium terms as touched for validation display
    if (this.preDefinedPremiumTerms) {
      this.premiumTermsTouched = true;
    }

    // Block submission if premium terms are required but empty
    if (this.isPremiumTermsInvalid) {
      return;
    }

    this.processing = true;
    let authData = {
      ...this.authForm.value,
      premium_terms: this.premiumTerms
    };
    let url = 'admin/product-options/' + this.injected_data['product_option'].uuid;

    this.apiService.patch(url, authData).subscribe((res) => {
      console.log("Edit Provider Options response", res);
      if (!res.error) this.dialogRef.close(res);
      this.processing = false;
    });
  }

}
